import { useCallback, useMemo } from "react";
import type { DoczHandle } from "@norkron/web-docz";
import { articleApi } from "../../api/articleUnits";
import { buildDoczHandleFromBinary } from "../../lib/doczHandle";
import { ReadCanvas } from "./ReadCanvas";
// The surface's OWN chrome — imported here, not restated. The history-panel
// overlay does not render `<DoczCanvas>` (which is where `readCanvas.css` was
// pulled in), so without this the version sheet would mount unstyled outside the
// workbench: no page column, no paper shadow, no horizontal-scroll rule.
import "./readCanvas.css";
import "./versionCanvas.css";

/**
 * `<VersionCanvas>` — A PAST REVISION, PAINTED ON THE CANVAS ENGINE.
 *
 * Owner decision D-E: **no DOM renders any docz IR anywhere**, version history and
 * diff views explicitly included. Read mode pivoted to canvas on 2026-07-28; the
 * two surfaces that show the document AT ANOTHER REVISION were the last ones left
 * on `dangerouslySetInnerHTML`:
 *
 *   * `<VersionHistoryPanel>`'s read-only viewer (the history rail)
 *   * `<ArticleWorkbench>`'s past-version pane (a timeline tick)
 *
 * Both now mount this.
 *
 * ## Why this file is twelve lines of logic and not a second canvas surface
 *
 * RULE ZERO / HR13. The mount, the wasm relayout, the cross-page drag-select,
 * double-click word select, ⌘A, the IR-sliced copy, the accessible
 * `DocumentMirror`, the presentation-policy scale resolution and the honest
 * failure pane are ALL `<ReadCanvas>`'s already — which is itself thin glue over
 * `@norkron/web-page-layout`'s `createCanvasSurface`. `<ReadCanvas>` exposes a
 * `loadHandle` seam precisely so the document it paints can come from somewhere
 * other than the head, so a past revision is a DIFFERENT TRANSPORT through the
 * SAME surface — not a second surface.
 *
 * Building a parallel `VersionCanvas` that called `createCanvasSurface` itself is
 * exactly the shape that produced the scale bug this codebase just paid for: two
 * mounts of one engine, each deciding its own presentation
 * (`canvasPresentation.ts`). A version sheet at a different scale than the head
 * sheet would be that bug again, and it cannot happen here because there is only
 * one mount.
 *
 * ## The transport
 *
 * `articleApi.getVersionDoczBinary` — `GET /api/documents/:id/versions/:vid` with
 * `Accept: application/vnd.norkron.document;v=1`. Raw docz block-DTO bytes,
 * BYTE-IDENTICAL to the head's binary representation for identical stored forms
 * (asserted server-side in `src/tests/e2e_version_representation.zig`). Most
 * version rows carry no structured blob at rest, so the server DERIVES the IR from
 * the archived HTML on read and never writes it — the archive is the one thing
 * that must not be rewritten.
 *
 * ## What is deliberately NOT here
 *
 * **No comments, no anchors, no composer.** `comments={[]}`, and no
 * `onSelectText` / `onAnchorStates` / `onSelectComment`. A comment anchors to the
 * HEAD's block ordinals; painting live comment bands over a historical revision
 * would place them by coincidence, and a composer over a read-only archive would
 * offer a write that cannot land. Selection and copy still work — they come from
 * the IR, not from the annotation layer.
 *
 * **No revision bump.** An archived version is immutable, so there is nothing to
 * re-fetch: the surface remounts when `versionId` changes and never otherwise.
 *
 * ## What does not come across (inherited, and stated rather than hidden)
 *
 * Browser find-in-page (⌘F), native copy of a DOM range, print (⌘P), browser
 * translation, extensions/user stylesheets and browser text zoom are DOM
 * affordances a `<canvas>` does not have. `DocumentMirror` keeps the revision
 * readable to assistive technology; it restores none of the above. This is the
 * same trade read mode makes, and it is the same trade because it is the same
 * surface.
 */

export interface VersionCanvasProps {
  /** The document the revision belongs to. */
  readonly docId: string | number;
  /** The `el.doc_versions.id` to paint. */
  readonly versionId: number;
  /** Accessible name for the mirror's `role="document"` landmark — say WHICH
   *  revision, so a screen-reader user is not left assuming it is the head. */
  readonly title?: string;
  /** BCP-47 language of the document, so a German deliverable is not read out in
   *  an English voice (WCAG 3.1.2). */
  readonly lang?: string;
  /** Whether this surface is mounted/visible (a hidden pane does no wasm work). */
  readonly active?: boolean;
  readonly className?: string;
  readonly testId?: string;
  /** DI seam (tests / E2E): resolve the revision into a live `DoczHandle`. */
  readonly loadHandle?: (docId: string, versionId: number) => Promise<DoczHandle>;
}

/** The default transport: the negotiated binary representation of ONE version. */
async function defaultLoadVersionHandle(
  docId: string,
  versionId: number,
): Promise<DoczHandle> {
  return buildDoczHandleFromBinary(
    await articleApi.getVersionDoczBinary(docId, versionId),
  );
}

export function VersionCanvas({
  docId,
  versionId,
  title,
  lang,
  active = true,
  className,
  testId = "version-canvas",
  loadHandle = defaultLoadVersionHandle,
}: VersionCanvasProps) {
  // Bind the revision into `<ReadCanvas>`'s `(docId) => Promise<DoczHandle>`
  // seam. Memoised on the identity pair because `<ReadCanvas>` lists `loadHandle`
  // in its mount effect's dependencies — a fresh function each render would tear
  // the surface down and re-run the whole wasm layout on every parent render.
  const load = useCallback(
    (id: string) => loadHandle(id, versionId),
    [loadHandle, versionId],
  );

  // A stable empty comment set, for the same reason: a new `[]` each render is a
  // new identity and would re-run the re-anchor pass forever.
  const noComments = useMemo(() => [], []);

  return (
    <div
      className={
        className ? `ela-version-canvas ${className}` : "ela-version-canvas"
      }
      data-testid={testId}
      data-version-surface="canvas"
      data-version-id={String(versionId)}
    >
      <ReadCanvas
        docId={docId}
        comments={noComments}
        active={active}
        {...(title !== undefined ? { title } : {})}
        {...(lang !== undefined ? { lang } : {})}
        loadHandle={load}
        testId={`${testId}-surface`}
      />
    </div>
  );
}
